import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineExclamationTriangle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

const LossTypes = () => {
  const [lossTypes, setLossTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLossType, setEditingLossType] = useState(null);
  const [formData, setFormData] = useState({
    lossTypeName: '',
    category: '',
    oeeImpact: '',
    description: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadLossTypes();
    } else {
      setError('Please login to view loss types');
      setLoading(false);
    }
  }, [tenantId]);

  const loadLossTypes = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getLossTypes(tenantId);
      setLossTypes(data);
    } catch (err) {
      setError('Failed to load loss types. Please try again.');
      console.error('Error loading loss types:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredLossTypes = lossTypes.filter((lossType) =>
    lossType.lossTypeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lossType.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lossType.oeeImpact?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => (a.serialNo || 0) - (b.serialNo || 0));

  const handleAdd = () => {
    setEditingLossType(null);
    setFormData({
      lossTypeName: '',
      category: '',
      oeeImpact: '',
      description: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (lossType) => {
    setEditingLossType(lossType);
    setFormData({
      lossTypeName: lossType.lossTypeName || '',
      category: lossType.category || '',
      oeeImpact: lossType.oeeImpact || '',
      description: lossType.description || '',
      status: lossType.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteLossType(id);
      await loadLossTypes();
      setDeleteConfirm(null);
    } catch (err) {
      let errorMessage = 'Failed to delete loss type. Please try again.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      setError(errorMessage);
      console.error('Error deleting loss type:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    const nameRegex = /^[A-Za-z0-9\s().,/-]+$/;

    if (!formData.lossTypeName.trim()) {
      errors.lossTypeName = 'Loss type name is required';
    } else if (formData.lossTypeName.length > 100) {
      errors.lossTypeName = 'Loss type name must be less than 100 characters';
    } else if (!nameRegex.test(formData.lossTypeName.trim())) {
      errors.lossTypeName = 'Name contains invalid characters';
    } else {
      const isDuplicate = lossTypes.some(
        (lt) =>
          lt.lossTypeName?.toLowerCase() === formData.lossTypeName.trim().toLowerCase() &&
          lt.lossTypeId !== editingLossType?.lossTypeId
      );
      if (isDuplicate) {
        errors.lossTypeName = 'A loss type with this name already exists';
      }
    }

    if (!formData.category) {
      errors.category = 'Category is required';
    }

    if (!formData.oeeImpact) {
      errors.oeeImpact = 'OEE impact is required';
    }

    if (formData.description && formData.description.length > 255) {
      errors.description = 'Description must be less than 255 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const lossTypeData = {
        tenantId: tenantId,
        lossTypeName: formData.lossTypeName.trim(),
        category: formData.category.trim() || null,
        oeeImpact: formData.oeeImpact.trim() || null,
        description: formData.description.trim() || null,
        status: formData.status,
        subTypes: [] // Navigation property
      };

      if (editingLossType) {
        lossTypeData.lossTypeId = editingLossType.lossTypeId;
        await masterDataService.updateLossType(editingLossType.lossTypeId, lossTypeData);
      } else {
        await masterDataService.createLossType(lossTypeData);
      }

      await loadLossTypes();
      setShowModal(false);
      setFormData({ lossTypeName: '', category: '', oeeImpact: '', description: '', status: 'Active' });
      setEditingLossType(null);
    } catch (err) {
      let errorMessage = 'Failed to save loss type. Please try again.';
      
      if (err.response?.data) {
        // Check for message string
        if (typeof err.response.data.message === 'string') {
          errorMessage = err.response.data.message;
        }
        // Check for errors object - extract first error message
        else if (err.response.data.errors) {
          const errors = err.response.data.errors;
          if (typeof errors === 'string') {
            errorMessage = errors;
          } else if (typeof errors === 'object') {
            // Extract first error message from object
            const firstErrorKey = Object.keys(errors)[0];
            if (firstErrorKey) {
              const firstError = errors[firstErrorKey];
              if (Array.isArray(firstError)) {
                errorMessage = firstError[0] || errorMessage;
              } else if (typeof firstError === 'string') {
                errorMessage = firstError;
              } else {
                errorMessage = `Validation error: ${firstErrorKey}`;
              }
            }
          }
        }
        // Check for error string
        else if (typeof err.response.data.error === 'string') {
          errorMessage = err.response.data.error;
        }
      } else if (err.message && typeof err.message === 'string') {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLossType(null);
    setFormData({ lossTypeName: '', category: '', oeeImpact: '', description: '', status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading loss types..." />;
  }

  return (
    <div className="loss-types-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Loss Types</h2>
          <p>Manage loss types for downtime tracking</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus className="btn-icon" />
          Add Loss Type
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search loss types..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredLossTypes.length} {filteredLossTypes.length === 1 ? 'loss type' : 'loss types'}
          </div>
        </div>

        {filteredLossTypes.length === 0 ? (
          <div className="empty-state">
            <HiOutlineExclamationTriangle className="empty-icon" />
            <h3>No loss types found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first loss type'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                New Loss Type
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Loss Type Name</th>
                <th>Category</th>
                <th>OEE Impact</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLossTypes.map((lossType, index) => (
                <tr key={lossType.lossTypeId}>
                  <td>{lossType.serialNo || '-'}</td>
                  <td className="name-cell">{lossType.lossTypeName}</td>
                  <td>{lossType.category || '-'}</td>
                  <td>{lossType.oeeImpact || '-'}</td>
                  <td className="description-cell">{lossType.description || '-'}</td>
                  <td>
                    <span className={`status-badge ${lossType.status?.toLowerCase()}`}>
                      {lossType.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {lossType.createdAt
                      ? new Date(lossType.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(lossType)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(lossType)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingLossType ? 'Edit Loss Type' : 'New Loss Type'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</button>
            <button type="submit" form="loss-type-form" className="btn-primary" disabled={submitting}>
              {submitting ? (editingLossType ? 'Updating...' : 'Creating...') : (editingLossType ? 'Update' : 'Create')}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="loss-type-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="lossTypeName">
                Loss Type Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="lossTypeName"
                value={formData.lossTypeName}
                onChange={(e) =>
                  setFormData({ ...formData, lossTypeName: e.target.value })
                }
                className={formErrors.lossTypeName ? 'error' : ''}
                placeholder="e.g., Breakdown, Setup, Material Shortage"
                maxLength={100}
              />
              {formErrors.lossTypeName && (
                <span className="field-error">{formErrors.lossTypeName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                className={formErrors.category ? 'error' : ''}
              >
                <option value="">Select Category</option>
                <option value="Equipment">Equipment</option>
                <option value="Manpower">Manpower</option>
                <option value="Material">Material</option>
                <option value="Quality">Quality</option>
                <option value="Energy">Energy</option>
                <option value="Tooling">Tooling</option>
                <option value="System">System</option>
              </select>
              {formErrors.category && (
                <span className="field-error">{formErrors.category}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="oeeImpact">OEE Impact</label>
              <select
                id="oeeImpact"
                value={formData.oeeImpact}
                onChange={(e) =>
                  setFormData({ ...formData, oeeImpact: e.target.value })
                }
                className={formErrors.oeeImpact ? 'error' : ''}
              >
                <option value="">Select Impact</option>
                <option value="Availability">Availability</option>
                <option value="Performance">Performance</option>
                <option value="Quality">Quality</option>
              </select>
              {formErrors.oeeImpact && (
                <span className="field-error">{formErrors.oeeImpact}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter description"
                rows="3"
                maxLength={255}
                className={formErrors.description ? 'error' : ''}
              />
              {formErrors.description && (
                <span className="field-error">{formErrors.description}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.lossTypeId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.lossTypeName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The loss type will be permanently deleted.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default LossTypes;

